/**
 * A change of screen (title -> story -> level 1, game over -> title, the ending, quitting) passes
 * through a short transition: the view covers over, the change happens while it is covered, and it
 * uncovers onto the new screen. The session runs it in 50 Hz frames; the view draws it
 * (`GameView.setTransition`) at the display's own rate from `cover(acc)`.
 */

/** Frames to cover, and the same again to uncover (0.2 s each). */
const HALF = 10;

export class Transition {
  private t = -1;
  private action: (() => void) | null = null;

  /** Cover, do `action`, uncover. Ignored while one is running. */
  run(action: () => void): void {
    if (this.active) return;
    this.t = 0;
    this.action = action;
  }

  /** Running at all. */
  get active(): boolean {
    return this.t >= 0;
  }

  /** Still covering over: the old screen holds still. */
  get holding(): boolean {
    return this.t >= 0 && this.t < HALF;
  }

  /** One 50 Hz frame. */
  tick(): void {
    if (this.t < 0) return;
    this.t++;
    if (this.t === HALF) {
      const a = this.action;
      this.action = null;
      a?.();
    }
    if (this.t >= 2 * HALF) this.t = -1;
  }

  /** How covered the view is, 0..1, `acc` of the way to the next frame. */
  cover(acc: number): number {
    if (this.t < 0) return 0;
    const f = Math.min(2 * HALF, this.t + acc);
    return f <= HALF ? f / HALF : Math.max(0, 2 - f / HALF);
  }
}
